import { createContext, useCallback, useContext, useState } from 'react';

type ToastConfig = {
  message: string | null;
  showIcon?: boolean;
  duration?: number;
  position?: 'center' | 'left' | 'right';
  variant?: 'neutral' | 'error';
  action?: {
    label: string;
    onClick: () => void;
  };
};

type ToastContextType = {
  showToast: (config: ToastConfig) => void;
  resetToast: () => void;
  toastConfig: ToastConfig;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const DEFAULT_TOAST_CONFIG: ToastConfig = {
  message: null,
  duration: 3000,
  position: 'center',
  variant: 'neutral',
  showIcon: false,
};

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toastConfig, setToastConfig] = useState<ToastConfig>(DEFAULT_TOAST_CONFIG);

  const showToast = (config: ToastConfig) => {
    setToastConfig({ ...DEFAULT_TOAST_CONFIG, ...config });
  };

  const resetToast = useCallback(() => {
    setToastConfig(DEFAULT_TOAST_CONFIG);
  }, []);

  return (
    <ToastContext.Provider value={{ toastConfig, showToast, resetToast }}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
